import { Action, createHook, createStore } from "react-sweet-state";

import { authenticatedPostFetch } from "utility/authenticated_fetch";
import { captureSentryException } from "utility/capture_sentry_exception";

// -----------------------------------------------------------------------------

export enum OAuth2Progress {
  Unknown,
  Submitting,
  ErrorOAuthParam,
  Error,
  Success,
}

export enum OAuth2URLSuffix {
  AZURE = "api/organization/azure",
  BOX = "api/organization/box",
  GITHUB = "api/organization/github",
  GOOGLE = "api/organization/google",
  MICROSOFT_365 = "api/organization/m365",
  PAGERDUTY = "api/organization/pagerduty",
  SLACK = "api/organization/slack",
}

// -----------------------------------------------------------------------------

export const initialState: OAuth2StoreState = {
  progress: OAuth2Progress.Unknown,
};

// -----------------------------------------------------------------------------

export const useOAuth2Store = createHook(
  createStore({
    initialState,
    actions: {
      submitParams,
    },
  })
);

// -----------------------------------------------------------------------------

function submitParams(
  urlSuffix: OAuth2URLSuffix,
  oauthCode: string | null,
  oauthState: string | null,
  other?: { [k: string]: string }
): Action<OAuth2StoreState> {
  return async ({ getState, setState }) => {
    // catch just in case
    const { progress } = getState();
    if (progress === OAuth2Progress.Submitting) {
      return;
    }

    if (other != null) {
      // values must be one of the following: https://datatracker.ietf.org/doc/html/rfc6749#section-4.1.2.1
      switch (other.error) {
        case "access_denied":
          return setState({
            progress: OAuth2Progress.ErrorOAuthParam,
            errorDetail:
              "You either do not have sufficient permissions in the source to connect it to ThreatKey or have cancelled the connection process.",
          });
        default:
          break;
      }
    }

    if (!oauthCode) {
      return setState({
        progress: OAuth2Progress.ErrorOAuthParam,
        errorDetail: "code is missing or empty",
      });
    }

    if (!oauthState) {
      return setState({
        progress: OAuth2Progress.ErrorOAuthParam,
        errorDetail: "state is missing or empty",
      });
    }

    try {
      setState({ progress: OAuth2Progress.Submitting });
      const responsePayload = await authenticatedPostFetch<{
        secret_uuid: string;
      }>(urlSuffix, {
        code: oauthCode,
        state: oauthState,
        ...other,
      });
      setState({
        progress: OAuth2Progress.Success,
        uuid: responsePayload.secret_uuid,
        errorDetail: undefined,
      });
    } catch (error) {
      captureSentryException(error, "Failed to submit OAuth params");
      setState({
        progress: OAuth2Progress.Error,
        errorDetail: typeof error === "string" ? error : undefined,
      });
    }
  };
}

// -----------------------------------------------------------------------------

interface OAuth2StoreState {
  progress: OAuth2Progress;
  errorDetail?: string;
  uuid?: string;
}
